import React, { useEffect, useMemo, useState } from 'react';
import { Select, Form } from 'antd';
// Hooks
import { useGetSports } from 'hooks';

const { Option } = Select;

type SportsSelectProps = {
  name: string;
  label: string;
  placeholder: string;
  required?: boolean;
  initialValue?: string[] | null;
  onChange?: (value: string[]) => void;
};

const SportsSelect: React.FC<SportsSelectProps> = ({
  name,
  label,
  placeholder,
  required = false,
  initialValue = [],
  onChange,
}) => {
  const [shouldShowAllSportsOption, setShouldShowAllSportsOption] =
    useState(true);

  const { data: sportsData, loading: loadingSports } = useGetSports();

  const sportsDataSports = sportsData?.sports;
  const sports = useMemo(() => sportsDataSports || [], [sportsDataSports]);

  const sportsOptions = useMemo(() => {
    const allSportsOption = {
      id: 0,
      name: 'All Sports',
      __typename: 'UserSport' as const,
      hashtags: null,
    };

    return shouldShowAllSportsOption ? [allSportsOption, ...sports] : sports;
  }, [shouldShowAllSportsOption, sports]);

  // Fill value from initial value if it exists
  useEffect(() => {
    if (initialValue?.length && sports.length > 0) {
      if (initialValue.length === sports.length) {
        setShouldShowAllSportsOption(false);
      }
    }
  }, [initialValue, sports]);

  // Custom handler for sports selection
  const handleSportsChange = (selectedValues: string[]) => {
    const allSportsOption = selectedValues.find(
      (sport) => sport === 'All Sports'
    );

    if (allSportsOption) {
      const actualSports = sports.map((sport) => sport.name);
      setShouldShowAllSportsOption(false);
      onChange?.(actualSports);
      return actualSports;
    } else {
      const selectedActualSports = selectedValues.filter(
        (sport) => sport !== 'All Sports'
      );

      const allSelected =
        selectedActualSports.length === sports.length && sports.length > 0;
      setShouldShowAllSportsOption(!allSelected);

      onChange?.(selectedActualSports);
      return selectedActualSports;
    }
  };

  return (
    <Form.Item
      name={name}
      label={label}
      getValueFromEvent={handleSportsChange}
      rules={[
        ...(required ? [{ required: true, message: 'Sport is required' }] : []),
      ]}
    >
      <Select
        mode="multiple"
        placeholder={placeholder}
        loading={loadingSports}
        filterOption={(inputValue, option) =>
          option?.props.children
            .toString()
            .toLowerCase()
            .includes(inputValue.toLowerCase())
        }
        allowClear
      >
        {sportsOptions.map((sport) => (
          <Option value={sport.name} key={sport.id}>
            {sport.name}
          </Option>
        ))}
      </Select>
    </Form.Item>
  );
};

export default SportsSelect;
